import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Crown, Globe, Flag } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";

interface UpgradeNationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}

export default function UpgradeNationModal({ isOpen, onClose, onUpgrade }: UpgradeNationModalProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    onUpgrade();
  };

  const handleViewPricing = () => {
    onClose();
    navigate("/pricing");
  };

  const Content = () => (
    <div className="flex flex-col items-center text-center py-4">
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500/20 to-primary/20 flex items-center justify-center">
          <Globe className="w-10 h-10 text-primary" />
        </div>
        <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
          <Crown className="w-4 h-4 text-black" />
        </div>
      </div>

      <h3 className="font-display text-xl font-bold text-foreground mb-2">
        Personalize Your Dashboard
      </h3>
      <p className="text-muted-foreground mb-6 max-w-sm">
        Set your favorite nation to see their games, players, and receive alerts. 
        This feature is available for Pro and Elite subscribers.
      </p>

      <div className="w-full space-y-3">
        <Button 
          onClick={handleUpgrade} 
          className="w-full gap-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
        >
          <Crown className="w-4 h-4" />
          Upgrade to Pro or Elite
        </Button>
        <Button 
          onClick={handleViewPricing} 
          variant="outline" 
          className="w-full"
        >
          View All Plans
        </Button>
      </div>

      <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
        <div className="flex gap-1">
          <span className="text-lg">🇨🇦</span>
          <span className="text-lg">🇸🇪</span>
          <span className="text-lg">🇫🇮</span>
          <span className="text-lg">🇺🇸</span>
        </div>
        <span>24 nations available</span>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="px-4 pb-6">
          <DrawerHeader className="text-center">
            <DrawerTitle className="sr-only">Upgrade Required</DrawerTitle>
            <DrawerDescription className="sr-only">
              Upgrade to Pro or Elite to set your favorite nation
            </DrawerDescription>
          </DrawerHeader>
          <Content />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="sr-only">Upgrade Required</DialogTitle>
          <DialogDescription className="sr-only">
            Upgrade to Pro or Elite to set your favorite nation
          </DialogDescription>
        </DialogHeader>
        <Content />
      </DialogContent>
    </Dialog>
  );
}
